/**
 * Self-heals the native test-bridge "Agent: reading app state" kill banner.
 *
 * The Android test-bridge injects `#__agent-kill-banner__` into the WebView on
 * every `/eval` and `/screenshot`, and is supposed to remove it via a
 * main-thread idle timer. That timer does NOT fire reliably once AgentChat is
 * backgrounded (Android defers delayed callbacks), and agents that drive the
 * bridge with raw HTTP — rather than `mobile-test-apk.sh`, which cleans up via
 * a shell trap — never send the "session done" signal at all. Net result: when
 * an agent run ends the amber banner can sit on screen indefinitely. Mark hits
 * this "every time".
 *
 * The banner is plain DOM inside the page we control, so the page owns the fix:
 * sweep the banner once it has lived longer than {@link STALE_MS} without being
 * re-injected. The native bridge re-creates the banner on its next request (its
 * guard is `if(getElementById(...))return`), so:
 *   - an ACTIVE agent's banner reappears within a request — Mark keeps the Stop
 *     affordance while a run is genuinely in progress;
 *   - a FINISHED agent's banner is swept and never comes back.
 *
 * Reopening the app (`visibilitychange` → visible) is the canonical moment Mark
 * notices a stuck banner, and is exactly when the backgrounded native timer
 * failed to fire — so that path sweeps a clearly-stale banner within seconds.
 *
 * This is intentionally resilient to an out-of-date installed APK: it needs no
 * bridge cooperation, only the banner's well-known element id.
 */
const BANNER_ID = '__agent-kill-banner__'
const FIRST_SEEN_ATTR = 'data-wd-first-seen'

// How long a banner may persist (without the bridge re-creating it) before the
// page treats it as stale and removes it. Long enough that an active agent with
// normal request cadence keeps it visible; short enough that a finished run
// clears quickly. The bridge re-injects on its next request, so this is also
// the worst-case "flicker" gap during a genuinely active session.
const STALE_MS = 15_000
// On app-foreground a banner older than this is almost certainly left over from
// a run that ended while backgrounded — clear it promptly.
const FOREGROUND_GRACE_MS = 4_000
const SWEEP_INTERVAL_MS = 3_000

function sweep(graceMs: number): void {
  const el = document.getElementById(BANNER_ID)
  if (!el) return
  const seen = Number(el.getAttribute(FIRST_SEEN_ATTR) || '0')
  const now = Date.now()
  if (!seen) {
    // First time this banner instance is observed — stamp it. A fresh banner
    // the bridge injects after we remove one is untagged, so its clock restarts.
    el.setAttribute(FIRST_SEEN_ATTR, String(now))
    return
  }
  if (now - seen > graceMs) el.remove()
}

let started = false

/** Start the banner watchdog. Idempotent; no-op outside a browser. */
export function startAgentBannerWatchdog(): void {
  if (started || typeof window === 'undefined' || typeof document === 'undefined') return
  started = true
  window.setInterval(() => sweep(STALE_MS), SWEEP_INTERVAL_MS)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') sweep(FOREGROUND_GRACE_MS)
  })
}

// Exported for unit testing the staleness logic without timers.
export const __test = { sweep, BANNER_ID, FIRST_SEEN_ATTR, STALE_MS, FOREGROUND_GRACE_MS }
